import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp, 
  writeBatch,
  limit as firestoreLimit
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Lesson, Category, UserProgress, NewLesson, NewCategory, LessonFormat } from '../types/ai-devquest';
import { firestoreWrapper } from './firestoreWrapper';

// Collection names
const LESSONS_COLLECTION = 'ai-devquest-lessons';
const CATEGORIES_COLLECTION = 'ai-devquest-categories';
const USER_PROGRESS_COLLECTION = 'ai-devquest-user-progress';

/**
 * Utility function to remove undefined values from an object
 * Firestore doesn't accept undefined values
 */
const removeUndefinedValues = (obj: any): any => {
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(removeUndefinedValues);
  }
  
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== undefined) {
      acc[key] = removeUndefinedValues(value);
    }
    return acc;
  }, {} as any);
};

// Default categories
const DEFAULT_CATEGORIES = [
  {
    name: 'Getting Started',
    description: 'Introduction to AI coding concepts'
  },
  {
    name: 'Prompt Engineering',
    description: 'Learn how to craft effective prompts for AI coding'
  },
  {
    name: 'Code Generation',
    description: 'Techniques for generating code with AI'
  },
  {
    name: 'Code Refinement',
    description: 'Improving and refining AI-generated code'
  },
  {
    name: 'Advanced Techniques',
    description: 'Advanced AI coding techniques and patterns'
  }
];

/**
 * Service for AI DevQuest app
 */
export const aiDevQuestService = {
  /**
   * Get all lessons
   */
  getLessons: async (): Promise<Lesson[]> => {
    try {
      const q = query(
        collection(db, LESSONS_COLLECTION),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await firestoreWrapper.getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Lesson[];
    } catch (error) {
      
      throw error;
    }
  },
  
  /**
   * Get all categories
   */
  getCategories: async (): Promise<Category[]> => {
    try {
      
      
      // First, check if any categories are missing the order field
      const allCategoriesQuery = query(collection(db, CATEGORIES_COLLECTION));
      const allCategoriesSnapshot = await firestoreWrapper.getDocs(allCategoriesQuery);
      
      
      
      // Check if any categories are missing the order field
      const categoriesWithoutOrder = allCategoriesSnapshot.docs.filter(doc => {
        const data = doc.data();
        return data.order === undefined || data.order === null;
      });
      
      // If any categories are missing the order field, update them
      if (categoriesWithoutOrder.length > 0) {
        
        
        const batch = writeBatch(db);
        let maxOrder = -1;
        
        // Find the maximum order value
        allCategoriesSnapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.order !== undefined && data.order !== null && data.order > maxOrder) {
            maxOrder = data.order;
          }
        });
        
        // Update categories without order
        categoriesWithoutOrder.forEach((doc, index) => {
          const categoryRef = doc.ref;
          batch.update(categoryRef, { 
            order: maxOrder + index + 1,
            updatedAt: serverTimestamp()
          });
        });
        
        await batch.commit();
        
      }
      
      // Now get all categories with ordering
      const orderedQuery = query(
        collection(db, CATEGORIES_COLLECTION),
        orderBy('order', 'asc')
      );
      
      const orderedSnapshot = await firestoreWrapper.getDocs(orderedQuery);
      
      
      const categories = orderedSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Category[];
      
      
      return categories;
    } catch (error) {
      
      throw error;
    }
  },
  
  /**
   * Get user progress
   */
  getUserProgress: async (userEmail: string): Promise<UserProgress[]> => {
    try {
      const q = query(
        collection(db, USER_PROGRESS_COLLECTION),
        where('userId', '==', userEmail)
      );
      
      const querySnapshot = await firestoreWrapper.getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as UserProgress[];
    } catch (error) {
      
      throw error;
    }
  },
  
  /**
   * Add a new lesson
   */
  addLesson: async (lesson: NewLesson): Promise<string> => {
    try {
      // Create a new document reference with auto-generated ID
      const docRef = doc(collection(db, LESSONS_COLLECTION));
      
      // Set the document data
      await firestoreWrapper.setDoc(docRef, removeUndefinedValues({
        ...lesson,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }));
      
      return docRef.id;
    } catch (error) {
      
      throw error;
    }
  },
  
  /**
   * Update a lesson
   */
  updateLesson: async (id: string, lesson: Partial<NewLesson>): Promise<void> => {
    try {
      const lessonRef = doc(db, LESSONS_COLLECTION, id);
      
      await firestoreWrapper.updateDoc(lessonRef, {
        ...removeUndefinedValues(lesson),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      
      throw error;
    }
  },
  
  /**
   * Delete a lesson
   */
  deleteLesson: async (id: string): Promise<void> => {
    try {
      const lessonRef = doc(db, LESSONS_COLLECTION, id);
      
      await firestoreWrapper.deleteDoc(lessonRef);
      
      // Delete associated user progress
      const q = query(
        collection(db, USER_PROGRESS_COLLECTION),
        where('lessonId', '==', id)
      );
      
      const querySnapshot = await firestoreWrapper.getDocs(q);
      
      const batch = writeBatch(db);
      
      querySnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      await batch.commit();
    } catch (error) {
      
      throw error;
    }
  },
  
  /**
   * Add a new category
   */
  addCategory: async (category: NewCategory): Promise<string> => {
    try {
      // Get current categories to determine the next order value
      let nextOrder = 0;
      try {
        const categoriesSnapshot = await firestoreWrapper.getDocs(
          query(collection(db, CATEGORIES_COLLECTION), orderBy('order', 'desc'), firestoreLimit(1))
        );
        if (!categoriesSnapshot.empty) {
          const lastCategory = categoriesSnapshot.docs[0].data();
          nextOrder = (lastCategory.order || 0) + 1;
        }
      } catch (error) {
        
      }
      
      // Create a new document reference with auto-generated ID
      const docRef = doc(collection(db, CATEGORIES_COLLECTION));
      
      // Set the document data
      await firestoreWrapper.setDoc(docRef, {
        ...category,
        order: category.order !== undefined ? category.order : nextOrder,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return docRef.id;
    } catch (error) {
      
      throw error;
    }
  },
  
  /**
   * Update a category
   */
  updateCategory: async (id: string, category: Partial<NewCategory>): Promise<void> => {
    try {
      const categoryRef = doc(db, CATEGORIES_COLLECTION, id);
      
      await firestoreWrapper.updateDoc(categoryRef, {
        ...category,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      
      throw error;
    }
  },
  
  /**
   * Delete a category
   */
  deleteCategory: async (id: string): Promise<void> => {
    try {
      const categoryRef = doc(db, CATEGORIES_COLLECTION, id);
      
      // Find all lessons in this category
      const lessonsQuery = query(
        collection(db, LESSONS_COLLECTION),
        where('category', '==', id)
      );
      
      const lessonsSnapshot = await firestoreWrapper.getDocs(lessonsQuery);
      
      // Use a batch to delete the category and all its lessons
      const batch = writeBatch(db);
      
      // Delete the category
      batch.delete(categoryRef);
      
      // Delete all lessons in the category
      lessonsSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      // Commit the batch
      await batch.commit();
      
      // Note: We don't need to delete user progress records here as they will be orphaned
      // and won't affect the application functionality
    } catch (error) {
      
      throw error;
    }
  },
  
  /**
   * Update category orders
   */
  updateCategoryOrders: async (categoryOrders: { id: string, order: number }[]): Promise<void> => {
    try {
      const batch = writeBatch(db);
      
      categoryOrders.forEach(({ id, order }) => {
        const categoryRef = doc(db, CATEGORIES_COLLECTION, id);
        batch.update(categoryRef, { order, updatedAt: serverTimestamp() });
      });
      
      await batch.commit();
    } catch (error) {
      
      throw error;
    }
  },
  
  /**
   * Mark a lesson as completed
   */
  markLessonAsCompleted: async (userEmail: string, lessonId: string): Promise<void> => {
    try {
      // Check if progress already exists
      const q = query(
        collection(db, USER_PROGRESS_COLLECTION),
        where('userId', '==', userEmail),
        where('lessonId', '==', lessonId)
      );
      
      const querySnapshot = await firestoreWrapper.getDocs(q);
      
      if (querySnapshot.docs.length > 0) {
        // Update existing progress
        const progressRef = doc(db, USER_PROGRESS_COLLECTION, querySnapshot.docs[0].id);
        
        await firestoreWrapper.updateDoc(progressRef, {
          completed: true,
          completedAt: serverTimestamp()
        });
      } else {
        // Create new progress
        const progressRef = doc(collection(db, USER_PROGRESS_COLLECTION));
        await firestoreWrapper.setDoc(progressRef, {
          userId: userEmail,
          lessonId,
          completed: true,
          completedAt: serverTimestamp()
        });
      }
    } catch (error) {
      
      throw error;
    }
  },
  
  /**
   * Initialize default categories if they don't exist
   */
  initializeDefaultCategories: async (): Promise<void> => {
    try {
      // Check if categories already exist
      const categoriesSnapshot = await firestoreWrapper.getDocs(
        query(collection(db, CATEGORIES_COLLECTION))
      );
      
      if (categoriesSnapshot.docs.length === 0) {
        // No categories exist, create default ones
        
        const batch = writeBatch(db);
        
        DEFAULT_CATEGORIES.forEach((category, index) => {
          const docRef = doc(collection(db, CATEGORIES_COLLECTION));
          batch.set(docRef, {
            order: index,
            ...category,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        });
        
        try {
          await batch.commit();
        
  
        } catch (batchError) {
          
          throw batchError;
        }
        
        // Verify categories were created
        const verifySnapshot = await firestoreWrapper.getDocs(
          query(collection(db, CATEGORIES_COLLECTION))
        );
        
      } else {
        // Check if any categories are missing the order field
        const categoriesWithoutOrder = categoriesSnapshot.docs.filter(doc => {
          const data = doc.data();
          return data.order === undefined || data.order === null;
        });
        
        // If any categories are missing the order field, update them
        if (categoriesWithoutOrder.length > 0) {
          
          
          const batch = writeBatch(db);
          let maxOrder = -1;
          
          // Find the maximum order value
          categoriesSnapshot.docs.forEach(doc => {
            const data = doc.data();
            if (data.order !== undefined && data.order !== null && data.order > maxOrder) {
              maxOrder = data.order;
            }
          });
          
          // Update categories without order
          categoriesWithoutOrder.forEach((doc, index) => {
            const categoryRef = doc.ref;
            batch.update(categoryRef, { 
              order: maxOrder + index + 1,
              updatedAt: serverTimestamp()
            });
          });
          
          await batch.commit();
          
        }
      }
    } catch (error) {
      
      throw error;
    }
  },
  
  /**
   * Initialize sample lessons if they don't exist
   */
  initializeSampleLessons: async (): Promise<void> => {
    try {
      const lessonsSnapshot = await firestoreWrapper.getDocs(collection(db, LESSONS_COLLECTION));
      
      if (lessonsSnapshot.docs.length === 0) {
        // Get categories first
        const categoriesSnapshot = await firestoreWrapper.getDocs(collection(db, CATEGORIES_COLLECTION));
        const categories = categoriesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Category[];
        
        if (categories.length === 0) {
          
          return;
        }
        
        // Find category IDs by name
        const getCategoryId = (name: string): string => {
          const category = categories.find(c => c.name === name);
          return category ? category.id : categories[0].id;
        };
        
        // Sample lessons
        const sampleLessons = [
          {
            title: 'Introduction to AI Coding',
            description: 'Learn the basics of coding with AI assistants',
            category: getCategoryId('Getting Started'),
            icon: 'default',
            estimatedTime: 15,
            formats: [LessonFormat.VIDEO, LessonFormat.DOCUMENT],
            content: {
              videoUrl: 'https://vimeo.com/123456789',
              documentUrl: 'https://docs.google.com/document/d/123456789',
              notes: 'Welcome to AI DevQuest! This course will teach you how to code with AI.'
            }
          },
          {
            title: 'Crafting Effective Prompts',
            description: 'Learn how to write prompts that generate high-quality code',
            category: getCategoryId('Prompt Engineering'),
            icon: 'default',
            estimatedTime: 20,
            formats: [LessonFormat.VIDEO, LessonFormat.LINK],
            content: {
              videoUrl: 'https://vimeo.com/123456790',
              externalLinks: [
                'https://example.com/prompt-engineering',
                'https://example.com/prompt-templates'
              ]
            }
          },
          {
            title: 'Generating Complete Applications',
            description: 'Learn how to generate entire applications with AI',
            category: getCategoryId('Code Generation'),
            icon: 'default',
            estimatedTime: 30,
            formats: [LessonFormat.DOCUMENT],
            content: {
              documentUrl: 'https://docs.google.com/document/d/123456791'
            }
          }
        ];
        
        // Add sample lessons
        const batch = writeBatch(db);
        
        sampleLessons.forEach(lesson => {
          const docRef = doc(collection(db, LESSONS_COLLECTION));
          batch.set(docRef, {
            ...lesson,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        });
        
        await batch.commit();
        
        
      }
    } catch (error) {
      
      throw error;
    }
  }
};